import { useEffect, useRef, useState } from "react";
import type { Action, ActionRespondAction } from "../types";
import Rel from "../atoms/Rel";
import "./PanelActions.css";

export type ActionRespondPayload = {
  action: ActionRespondAction;
  reason_for_decline?: string;
};

type ActionRowProps = {
  a: Action;
  expanded: boolean;
  onToggle: () => void;
  onRespond: (a: Action, payload: ActionRespondPayload) => void;
  /** BOARDROOM-2026-08-08-124017 step 2: one-click dismiss, no reason
   *  required. Duncan must never be unable to clear a card — always
   *  rendered (not gated on age/priority), calling
   *  POST /api/cards/{ref}/dismiss. Optional for backward compat. */
  onDismiss?: (a: Action) => void;
};

function priorityClass(p: string): string {
  switch (p) {
    case "critical":
      return "is-critical";
    case "high":
      return "is-high";
    case "medium":
      return "is-medium";
    case "low":
      return "is-low";
    default:
      return "is-routine";
  }
}

function ActionRow({ a, expanded, onToggle, onRespond, onDismiss }: ActionRowProps) {
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineText, setDeclineText] = useState("");
  const declineRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (declineOpen && declineRef.current) declineRef.current.focus();
  }, [declineOpen]);

  const submit = (action: ActionRespondAction, reason?: string) => {
    setSubmitting(action);
    window.setTimeout(() => {
      onRespond(a, { action, reason_for_decline: reason });
    }, 350);
  };

  const submitDecline = () => {
    const text = declineText.trim();
    if (!text) return;
    setSubmitting("decline");
    window.setTimeout(() => {
      onRespond(a, { action: "decline", reason_for_decline: text });
      setDeclineOpen(false);
      setDeclineText("");
      setSubmitting(null);
    }, 350);
  };

  const rowId = `arow-${a.id}`;
  const detailId = `arow-detail-${a.id}`;
  const isParked = a.action_state === "parked";
  const isAwaitingConfirmation = a.action_state === "awaiting_done_confirmation";

  return (
    <div
      className={`arow ${expanded ? "is-open" : ""} ${submitting ? "is-submitting" : ""}`}
      data-action-id={a.id}
      id={rowId}
    >
      <div className="arow-main">
        <button
          type="button"
          className="arow-head"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={detailId}
        >
          <span className="arow-mark" aria-hidden="true">
            {expanded ? "▾" : "▸"}
          </span>
          <span className="arow-text">
            <span className="arow-title">{a.subject || a.id}</span>
            <span className="arow-id">{a.id}</span>
          </span>
          <span className="arow-origin">{a.origin}</span>
          <span className="arow-filed">
            <Rel t={a.filed} />
          </span>
        </button>
        {/* Collapsed-view inline hints per BDD E3 + E4 */}
        {(a.expected_completion || a.blocks_or_consequence) && (
          <div className="arow-hints">
            {a.expected_completion && (
              <span className="arow-hint">
                <span className="arow-meta-k">≤</span>{" "}
                {a.expected_completion}
              </span>
            )}
            {a.blocks_or_consequence && (
              <span className="arow-hint">
                <span className="arow-meta-k">Blocks:</span>{" "}
                {a.blocks_or_consequence}
              </span>
            )}
          </div>
        )}
        <div className="arow-actions" onClick={(e) => e.stopPropagation()}>
          <span className={`arow-priority ${priorityClass(a.priority)}`}>
            {a.priority || "routine"}
          </span>
          {isParked ? (
            <button
              type="button"
              className={`arow-btn ${submitting === "unpark" ? "is-busy" : ""}`}
              disabled={!!submitting}
              onClick={() => submit("unpark")}
              aria-label="Unpark"
            >
              {submitting === "unpark" ? "unparking…" : "Unpark"}
            </button>
          ) : isAwaitingConfirmation ? (
            /* BDD F6: entity reports done; Duncan confirms or disagrees */
            <>
              <button
                type="button"
                className={`arow-btn is-primary ${submitting === "mark_done" ? "is-busy" : ""}`}
                disabled={!!submitting}
                onClick={() => submit("mark_done")}
                aria-label="Mark Done"
              >
                {submitting === "mark_done" ? "marking…" : "Mark Done"}
              </button>
              <button
                type="button"
                className={`arow-btn ${submitting === "revert" ? "is-busy" : ""}`}
                disabled={!!submitting}
                onClick={() => submit("revert")}
                aria-label="Disagree"
              >
                {submitting === "revert" ? "reverting…" : "Disagree"}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={`arow-btn is-primary ${submitting === "mark_done" ? "is-busy" : ""}`}
                disabled={!!submitting}
                onClick={() => submit("mark_done")}
                aria-label="Mark Done"
              >
                {submitting === "mark_done" ? "marking…" : "Mark Done"}
              </button>
              <button
                type="button"
                className={`arow-btn ${submitting === "park" ? "is-busy" : ""}`}
                disabled={!!submitting}
                onClick={() => submit("park")}
                aria-label="Park"
              >
                {submitting === "park" ? "parking…" : "Park"}
              </button>
              <button
                type="button"
                className="arow-btn"
                disabled={!!submitting}
                onClick={() => setDeclineOpen((v) => !v)}
                aria-expanded={declineOpen}
                aria-label="Decline"
              >
                Decline
              </button>
            </>
          )}
          {/* BOARDROOM-2026-08-08-124017 step 2: rendered in EVERY branch
              (parked / awaiting-confirmation / awaiting-action) — Duncan
              must never be unable to clear a card, regardless of its
              current action_state. */}
          {onDismiss && (
            <button
              type="button"
              className="arow-btn arow-dismiss"
              disabled={!!submitting}
              onClick={() => onDismiss(a)}
              title="Clear this card — no reason required. It stops appearing everywhere (superseded)."
              aria-label="Dismiss"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="arow-detail" id={detailId}>
          {a.context && <div className="arow-context">{a.context}</div>}
          {a.action_steps && a.action_steps.length > 0 && (
            <ol className="arow-steps">
              {a.action_steps.map((s) => (
                <li key={s.n}>{s.body}</li>
              ))}
            </ol>
          )}
          <div className="arow-meta">
            {a.expected_completion && (
              <span>
                <span className="arow-meta-k">expected:</span>{" "}
                {a.expected_completion}
              </span>
            )}
            {a.blocks_or_consequence && (
              <span>
                <span className="arow-meta-k">blocks:</span>{" "}
                {a.blocks_or_consequence}
              </span>
            )}
          </div>
        </div>
      )}

      {declineOpen && (
        <div
          className="arow-decline"
          onClick={(e) => e.stopPropagation()}
        >
          <textarea
            ref={declineRef}
            value={declineText}
            onChange={(e) => setDeclineText(e.target.value)}
            placeholder="Reason for declining (required)"
            aria-label="Decline reason"
          />
          <div className="arow-decline-buttons">
            <button
              type="button"
              className="arow-btn"
              onClick={() => {
                setDeclineOpen(false);
                setDeclineText("");
              }}
            >
              cancel
            </button>
            <button
              type="button"
              className="arow-btn is-primary"
              disabled={!declineText.trim() || !!submitting}
              onClick={submitDecline}
            >
              {submitting === "decline" ? "declining…" : "Decline with reason"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PanelActions({
  actions,
  onRespond,
  recentlyDone = [],
  parked = [],
  recentlyDeclined = [],
  onDismiss,
}: {
  actions: Action[];
  onRespond?: (ref: string, payload: ActionRespondPayload) => Promise<void>;
  recentlyDone?: Action[];
  parked?: Action[];
  recentlyDeclined?: Action[];
  /** BOARDROOM-2026-08-08-124017 step 2: POST /api/cards/{ref}/dismiss.
   *  Optional — when absent, no Dismiss button renders (backward compat). */
  onDismiss?: (ref: string) => Promise<void>;
}) {
  const [list, setList] = useState<Action[]>(actions);
  const [openId, setOpenId] = useState<string | null>(null);
  const [respondError, setRespondError] = useState<string | null>(null);
  const [recentOpen, setRecentOpen] = useState(false);
  const [parkedOpen, setParkedOpen] = useState(false);
  const [declinedOpen, setDeclinedOpen] = useState(false);

  useEffect(() => {
    setList(actions);
  }, [actions]);

  const respond = (a: Action, payload: ActionRespondPayload) => {
    // Optimistic-hide for terminal transitions; unpark stays in list (it's
    // the parked sub-section's mutation, not the active list).
    if (payload.action !== "unpark") {
      setList((xs) => xs.filter((x) => x.id !== a.id));
      if (openId === a.id) setOpenId(null);
    }
    if (!onRespond) return;
    onRespond(a.id, payload).catch((err: unknown) => {
      setRespondError(err instanceof Error ? err.message : String(err));
    });
  };

  const dismiss = (a: Action) => {
    setList((xs) => xs.filter((x) => x.id !== a.id));
    if (openId === a.id) setOpenId(null);
    if (!onDismiss) return;
    onDismiss(a.id).catch((err: unknown) => {
      setRespondError(err instanceof Error ? err.message : String(err));
    });
  };

  return (
    <section
      className={`panel panel-actions ${list.length === 0 ? "is-empty" : "is-populated"}`}
      aria-labelledby="panel-actions-title"
    >
      <header className="panel-head">
        <h2 className="panel-title" id="panel-actions-title">
          Duncan actions
        </h2>
        <span className="panel-meta">
          <span className="panel-count">{list.length}</span>
          <span className="panel-sub">awaiting · polled every 15s</span>
        </span>
      </header>
      <div className="panel-body">
        {respondError && (
          <div
            className="panel-empty"
            style={{ color: "#fca5a5", fontSize: 11, padding: "6px 18px" }}
            role="alert"
          >
            Action failed: {respondError}
          </div>
        )}
        {list.length === 0 && (
          <div className="panel-empty">No actions pending.</div>
        )}
        {list.map((a) => (
          <ActionRow
            key={a.id}
            a={a}
            expanded={openId === a.id}
            onToggle={() => setOpenId((cur) => (cur === a.id ? null : a.id))}
            onRespond={respond}
            onDismiss={onDismiss ? dismiss : undefined}
          />
        ))}

        {recentlyDone.length > 0 && (
          <div className="panel-subsection">
            <button
              type="button"
              className="panel-subsection-toggle"
              onClick={() => setRecentOpen((v) => !v)}
              aria-expanded={recentOpen}
            >
              {recentOpen ? "▾" : "▸"} Recently done (last 24h){" "}
              <span className="panel-count">{recentlyDone.length}</span>
            </button>
            {recentOpen && (
              <ul className="panel-subsection-list">
                {recentlyDone.map((a) => (
                  <li key={a.id} data-action-id={a.id}>
                    <span className="arow-id">{a.id}</span> · {a.subject}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {parked.length > 0 && (
          <div className="panel-subsection">
            <button
              type="button"
              className="panel-subsection-toggle"
              onClick={() => setParkedOpen((v) => !v)}
              aria-expanded={parkedOpen}
            >
              {parkedOpen ? "▾" : "▸"} Parked{" "}
              <span className="panel-count">{parked.length}</span>
            </button>
            {parkedOpen && (
              <div>
                {parked.map((a) => (
                  <ActionRow
                    key={a.id}
                    a={a}
                    expanded={openId === a.id}
                    onToggle={() => setOpenId((cur) => (cur === a.id ? null : a.id))}
                    onRespond={respond}
                    onDismiss={onDismiss ? dismiss : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* BDD D3: recently-declined sub-section (24h window) with reason visible */}
        {recentlyDeclined.length > 0 && (
          <div className="panel-subsection">
            <button
              type="button"
              className="panel-subsection-toggle"
              onClick={() => setDeclinedOpen((v) => !v)}
              aria-expanded={declinedOpen}
            >
              {declinedOpen ? "▾" : "▸"} Recently declined (last 24h){" "}
              <span className="panel-count">{recentlyDeclined.length}</span>
            </button>
            {declinedOpen && (
              <ul className="panel-subsection-list">
                {recentlyDeclined.map((a) => (
                  <li key={a.id} data-action-id={a.id}>
                    <span className="arow-id">{a.id}</span> · {a.subject}
                    {a.reason_for_decline && (
                      <span className="arow-decline-reason">
                        {" "}— reason: {a.reason_for_decline}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
